import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { styled } from '@mui/material/styles';
import withDataLoaded from '../with_data_loaded';
import PageSpinner from '../shared/page_spinner';
import { userLoader } from '../../actions/data_loaders';
import Actions from '../../actions/actions';
import { useSelector } from 'react-redux';
import ImpersonationButton from '../admin/impersonation_button';
import {
  AssignmentInd as AssignmentIndIcon,
  Close as CloseIcon,
  DeleteOutlined as DeleteOutlinedIcon,
  PersonAdd as PersonAddIcon,
  RestoreOutlined as RestoreOutlinedIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import QAHiddenElement from '../ribbons/qahiddenelement';
import { DebounceInput } from 'react-debounce-input';
import LocationHelper from '../../helpers/location_helper';
import SearchHelper from '../../helpers/search_helper';
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import FeaturePermissionsHelper from '../../helpers/feature_permissions_helper';

const StyledUserList = styled(DataGridPro)(({ theme }) => ({
  fontFamily: ['sans-serif', 'Poppins'],
  border: 'none',
  borderRadius: 0,
  WebkitFontSmoothing: 'auto',
  letterSpacing: 'normal',
  '& .MuiInputBase-root': {
    height: '37px',
  },
  '& .MuiDataGrid-columnHeadersInner': {
    background: '#EAEAEA',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: '#2C6881',
    fontWeight: 700,
    fontSize: 13,
  },
  '& .MuiDataGrid-cell': {
    color: 'black',
    fontSize: 12,
    whiteSpace: 'normal',
  },
  '& .MuiDataGrid-row': {
    cursor: 'pointer',
    '&:nth-of-type(odd)': {
      backgroundColor: '#F6F6F6',
    },
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
}));

// const escapeRegExp = (value) => {
//   return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
// };

const ActivateDialog = (props) => {
  let activate = props.user === null ? false : props.user.active;

  return (
    <Dialog open={props.open}>
      <DialogTitle>
        Are you sure you want to
        {activate
          ? ' deactivate this user and prevent them from accessing the system?'
          : ' reactivate this user and reinstate their access to the system?'}
      </DialogTitle>

      <DialogActions>
        <Button id={'user-admin-activate-cancel'} onClick={() => props.onCancel(props.user)}>
          Cancel
        </Button>
        <Button
          id={'user-admin-activate-modal-confirm'}
          onClick={() => props.onAgree(props.user)}
          variant="contained"
          color="primary"
          autoFocus
        >
          Yes, {activate ? 'Deactivate' : 'Activate'}
        </Button>
        {activate && (
          <QAHiddenElement>
            <Button onClick={() => props.onDelete(props.user)} variant="contained" color="primary" autoFocus>
              Yes, Delete
            </Button>
          </QAHiddenElement>
        )}
      </DialogActions>
    </Dialog>
  );
};

const SearchInput = forwardRef((props, inputRef) => {
  return (
    <TextField
      inputRef={inputRef}
      style={{ width: '100%' }}
      id="user-list-search-input"
      onChange={props.onChange}
      value={props.value}
      variant="outlined"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon style={{ color: 'rgba(0,0,0,.3)' }} className="searchAdornmentIcon" />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => {
                props.hideSearch();
                props.clearSearch();
              }}
            >
              <CloseIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
});

const CustomToolbar = ({ value, coid, currUser, viewingActiveList, clearSearch, onChange }) => {
  const searchInputRef = useRef(null);
  const [showSearch, setShowSearch] = useState(false);
  const escFunction = useCallback(
    (event) => {
      if (event.keyCode === 27) {
        if (showSearch) {
          clearSearch();
          setShowSearch(false);
        }
      }
    },
    [showSearch, clearSearch]
  );

  useEffect(() => {
    if (showSearch) {
      searchInputRef.current.focus();
    }
    document.addEventListener('keydown', escFunction);
    return () => {
      document.removeEventListener('keydown', escFunction);
    };
  }, [showSearch, escFunction]);

  return (
    <>
      <Box
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        my="10px"
        px="16px"
      >
        <Box
          width="100%"
          maxWidth="350px"
          display="flex"
          alignItems="center"
          mb={{ xs: 2, md: 0 }}
          justifyContent={{ xs: 'center', sm: 'flex-start' }}
        >
          {showSearch ? (
            <DebounceInput
              inputRef={searchInputRef}
              variant="outlined"
              value={value}
              placeholder="Search"
              onChange={onChange}
              element={SearchInput}
              clearSearch={clearSearch}
              hideSearch={() => {
                setShowSearch(false);
              }}
            />
          ) : (
            <Typography variant="h6" style={{ fontSize: '28px', fontWeight: 300 }}>
              {!viewingActiveList && 'Inactive'} User List
            </Typography>
          )}
        </Box>
        <Box display="flex" alignItems="center" justifyContent="space-around">
          <Box className="toolbar-icon qa-user-list-search-button" alignItems="center" display="flex" flex="1">
            <IconButton
              data-testid="Search-iconButton"
              onClick={() => {
                searchInputRef.current?.value === '' ? setShowSearch(false) : setShowSearch(true);
              }}
            >
              <SearchIcon />
              <Typography fontSize="13px">Search</Typography>
            </IconButton>
          </Box>
          {(!!coid || currUser.atype === 'A') && viewingActiveList && (
            <>
              <button
                id="user-admin-list-add-user-button"
                style={{ display: 'flex' }}
                className="action-primary action-alt"
                onClick={() => Actions.setRoute(`/account/companies/${coid}/users/new`)}
                disabled={!FeaturePermissionsHelper.hasPermission(currUser, 'USER', 'CREATE')}
              >
                <PersonAddIcon className="add-user-icon" />
                Add User
              </button>
            </>
          )}
        </Box>
      </Box>
      <Box display="flex" justifyContent="flex-end" px="16px" mb="10px">
        <ToggleButtonGroup
          id="qa-user-admin-switch"
          color="standard"
          value={viewingActiveList}
          exclusive
          onChange={(event, value) => {
            const currListName = value ? 'active' : 'inactive';
            const company = coid ? `companies/${coid}/` : '';
            Actions.clearUser();
            if (value) {
              Actions.clearInactiveUserList();
            } else {
              Actions.clearActiveUserList();
            }
            Actions.fetchUserList({ isActive: value, coid });
            Actions.setRoute(`/account/${company}users?type=${currListName}`);
          }}
        >
          <ToggleButton
            id="qa-user-admin-switch-active"
            className="user-admin-padding-for-toggle-button user-admin-toggle-button"
            value={true}
          >
            Active
          </ToggleButton>
          <ToggleButton
            id="qa-user-admin-switch-inactive"
            className="user-admin-padding-for-toggle-button user-admin-toggle-button"
            value={false}
          >
            Inactive
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </>
  );
};

const convertUserForTable = (user) => {
  let atype = '';
  if (user.atype === 'CPF') {
    atype = 'CPF';
  } else if (user.atype === 'A') {
    atype = 'Admin';
  } else if (user.opsMgr) {
    atype = 'Ops Mgr';
  }

  return {
    ...user,
    fullname: `${user.firstname} ${user.lastname}`,
    companyname: user.company.name,
    role: atype,
    branchName: user?.branch?.branchname,
  };
};

export const UserList = () => {
  const [isActiveList] = useState(LocationHelper.searchAsObj()?.type !== 'inactive');
  const activeUsers = useSelector((redux) => redux.activeUserList);
  const inactiveUsers = useSelector((redux) => redux.inactiveUserList);
  const getCurrentUsers = useCallback(
    () => (isActiveList ? activeUsers : inactiveUsers),
    [activeUsers, inactiveUsers, isActiveList]
  );
  const coid = useSelector((redux) => redux.view?.coid);
  const currUser = useSelector((redux) => redux.user);
  let [userOfInterest, setUserOfInterest] = useState(null);
  let [isDialogOpen, setIsDialogOpen] = useState(false);

  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState('');
  const requestSearch = useCallback(
    (searchValue) => {
      setSearchText(searchValue);
      const filteredRows = SearchHelper.search(
        searchValue,
        getCurrentUsers(),
        ['id', 'fullname', 'username', 'companyName', 'role', 'branchName', 'pricingGroupLabel'],
        convertUserForTable
      );
      setRows(filteredRows);
    },
    [getCurrentUsers]
  );

  useEffect(() => {
    if (searchText) {
      requestSearch(searchText);
    } else {
      setRows(getCurrentUsers());
    }
  }, [getCurrentUsers, searchText, requestSearch]);

  let currUserAtype = currUser?.atype || '';
  const cpfCols =
    currUserAtype === 'CPF' ? [{ headerName: 'Company', field: 'companyname', minWidth: 180, flex: 1 }] : [];

  const columns = [
    { headerName: 'Name', field: 'fullname', minWidth: 120, sortable: true },
    { headerName: 'Email', field: 'username', minWidth: 250, flex: 1, sortable: true },
    ...cpfCols,
    { headerName: 'Role', field: 'role', minWidth: 40, sortable: true },
    {
      headerName: 'Branch',
      field: 'branch',
      sortable: true,
      valueGetter: ({ value }) => value.branchname,
    },
    {
      headerName: 'Product Option Group',
      headerClassName: 'nowrapHeader',
      headerAlign: 'center',
      field: 'pricingGroupLabel',
      sortable: true,
    },
    {
      headerName: 'Actions',
      field: 'title',
      headerAlign: 'center',
      align: 'center',
      minWidth: 85,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.userid === currUser.userid) {
          return <span id={row.atype} />; //placeholder to keep the height a static value
        }
        return (
          <Box display="flex">
            {isActiveList && row.atype !== 'CPF' && currUser.atype === 'CPF' && (
              <ImpersonationButton
                username={row.username}
                userid={row.userid}
                name={`${row.firstname} ${row.lastname}`}
                goto="/customers"
              >
                <IconButton aria-label="Login as User" title="Login as User">
                  <AssignmentIndIcon />
                </IconButton>
              </ImpersonationButton>
            )}
            {isActiveList && row.atype === 'CPF' && <div style={{ width: '45px' }} />}
            <span id={'qa-actions-deactivate'}>
              {(row.atype !== 'A' || currUser.atype === 'CPF') &&
                FeaturePermissionsHelper.hasPermission(currUser, 'USER', 'DELETE') && (
                  <IconButton
                    aria-label={isActiveList ? 'deactivate' : 'activate'}
                    title={isActiveList ? 'deactivate user' : 'activate user'}
                    onClick={(event, data) => {
                      event.stopPropagation();
                      setUserOfInterest(row);
                      setIsDialogOpen(true);
                    }}
                    disabled={!FeaturePermissionsHelper.hasPermission(currUser, 'USER', 'DELETE')}
                  >
                    {isActiveList ? <DeleteOutlinedIcon /> : <RestoreOutlinedIcon />}
                  </IconButton>
                )}
            </span>
          </Box>
        );
      },
    },
    { headerName: 'Coid', field: 'company', valueGetter: ({ value }) => value.coid },
    { headerName: 'Userid', field: 'userid' },
  ];

  return (
    <Box
      className="user-admin-page"
      id="qa-user-list-page"
      style={{
        display: 'flex',
        height: 'calc(100vh - 115px)',
        width: '100%',
        minWidth: '980px',
      }}
    >
      <StyledUserList
        onCellClick={(params) => {
          const userId = params.row.userid;
          if (params.field !== 'title') {
            if (userId !== 'undefined' && coid !== undefined) {
              Actions.setRoute(`/account/companies/${coid}/users/${userId}`);
            } else if (userId !== 'undefined') {
              Actions.setRoute(`/account/users/${userId}`);
            }
          }
        }}
        rowHeight={45}
        headerHeight={80}
        rows={(rows ?? []).map(convertUserForTable)}
        columns={columns}
        disableSelectionOnClick
        disableColumnReorder
        hideFooter
        disableColumnResize
        disableColumnMenu
        initialState={{
          columns: {
            columnVisibilityModel: {
              userid: false,
              company: false,
            },
          },
        }}
        components={{
          NoRowsOverlay: () => (
            <Box
              id="no-customers-found"
              display="flex"
              height="100%"
              alignItems="flex-start"
              justifyContent="center"
              pt={2}
            >
              <Typography>
                <b>No Users found</b>
              </Typography>
            </Box>
          ),
          LoadingOverlay: PageSpinner,
          Toolbar: CustomToolbar,
        }}
        componentsProps={{
          toolbar: {
            value: searchText,
            coid: coid,
            currUser: currUser,
            viewingActiveList: isActiveList,
            clearSearch: () => requestSearch(''),
            onChange: (event) => requestSearch(event.target.value),
          },
        }}
      />
      <ActivateDialog
        user={userOfInterest}
        open={isDialogOpen}
        onAgree={async () => {
          setIsDialogOpen(false);

          if (userOfInterest.active) {
            await Actions.deleteUser(userOfInterest.userid);
          } else {
            await Actions.activateUser(userOfInterest.userid);
          }
        }}
        onCancel={() => {
          setIsDialogOpen(false);
        }}
        onDelete={() => {
          Actions.hardDeleteUser(userOfInterest.userid);
        }}
      />
    </Box>
  );
};

const withUsersLoaded = withDataLoaded({
  WithData: UserList,
  WithoutData: PageSpinner,
  data: [
    userLoader,
    {
      storeKeys: ['activeUserList'],
      loadDataFn: ({ view }) => {
        Actions.fetchUserList({ isActive: true, coid: view.coid });
      },
    },
    {
      storeKeys: ['inactiveUserList'],
      loadDataFn: ({ view }) => {
        Actions.fetchUserList({ isActive: false, coid: view.coid });
      },
      emptyIsStillData: true,
    },
  ],
});

export default withUsersLoaded;
